import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiGet, apiPost, ChecklistFull, SubmissionSummary, SubmissionDetail } from './api';
import DataTable from '../../shared/components/DataTable';

interface ResponseDraft {
  is_completed: boolean;
  notes: string;
}

const ChecklistDetail: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const [tab, setTab] = useState<'fill' | 'history'>('fill');
  const [tpl, setTpl] = useState<ChecklistFull | null>(null);
  const [history, setHistory] = useState<SubmissionSummary[]>([]);
  const [view, setView] = useState<SubmissionDetail | null>(null);
  const [responses, setResponses] = useState<Record<number, ResponseDraft>>({});
  const [overallNotes, setOverallNotes] = useState('');
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [saving, setSaving] = useState(false);

  const [remaining, setRemaining] = useState(false);
  const [noteItems, setNoteItems] = useState<Set<number>>(new Set());
  useEditProtection(!!overallNotes || Object.values(responses).some(r => r.is_completed || !!r.notes));

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      const data = await apiGet<{ checklist: ChecklistFull }>(`/checklists/${slug}`, sessionToken);
      setTpl(data.checklist);
      const draft: Record<number, ResponseDraft> = {};
      for (const sec of data.checklist.sections) {
        for (const it of sec.items) draft[it.id] = { is_completed: false, notes: '' };
      }
      setResponses(draft);
    } catch (e: any) { setErr(e.message); }
  }, [slug, sessionToken]);

  const loadHistory = useCallback(async () => {
    if (!tpl) return;
    try {
      const data = await apiGet<{ submissions: SubmissionSummary[] }>(
        `/checklist-submissions?templateId=${tpl.id}&limit=100`,
        sessionToken
      );
      setHistory(data.submissions);
    } catch (e: any) { setErr(e.message); }
  }, [tpl, sessionToken]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  const setItem = (itemId: number, patch: Partial<ResponseDraft>) => {
    setResponses((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  };

  const submit = async () => {
    if (!tpl) return;
    try {
      setSaving(true);
      setErr('');
      setInfo('');
      const responsesArr = Object.entries(responses).map(([itemId, r]) => ({
        item_id: Number(itemId),
        is_completed: r.is_completed,
        notes: r.notes || null,
      }));
      const res = await apiPost<{ id: number }>(
        '/checklist-submissions',
        { template_id: tpl.id, notes: overallNotes || null, responses: responsesArr },
        sessionToken
      );
      setInfo(`Submitted (#${res.id}).`);
      // reset form + reload
      setOverallNotes('');
      await load();
      if (tab === 'history') await loadHistory();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openSubmission = async (id: number) => {
    try {
      const data = await apiGet<SubmissionDetail>(`/checklist-submissions/${id}`, sessionToken);
      setView(data);
    } catch (e: any) { setErr(e.message); }
  };

  if (!tpl) {
    return (
      <div>
        <Link to="/checklists">← All checklists</Link>
        {err ? <div className="error-banner">{err}</div> : <p>Loading…</p>}
      </div>
    );
  }

  // Group sections by group_name
  const grouped: Record<string, typeof tpl.sections> = {};
  for (const sec of tpl.sections) {
    const key = sec.group_name || '';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(sec);
  }

  const completedCount = Object.values(responses).filter((r) => r.is_completed).length;
  const totalCount = Object.keys(responses).length;
  const canViewAll = ['Owner', 'Admin', 'Manager'].includes(user?.role || '');

  return (
    <div style={{ maxWidth: 900 }}>
      <Link to="/checklists" style={{ display: 'inline-block', marginBottom: '0.5rem' }}>← All checklists</Link>
      <h2 style={{ marginBottom: 0 }}>{tpl.name}</h2>
      <p style={{ color: '#666', marginTop: 4 }}>{tpl.frequency}</p>

      {err && <div className="error-banner">{err}</div>}
      {info && <div style={{ background: '#e3f9e5', borderLeft: '4px solid #4caf50', padding: '0.5rem 1rem', marginBottom: '1rem' }}>{info}</div>}

      <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid #ddd', marginBottom: '1rem' }}>
        <button
          className={`salary-btn ${tab === 'fill' ? '' : 'secondary'}`}
          style={{ borderRadius: '4px 4px 0 0' }}
          onClick={() => setTab('fill')}
        >Fill</button>
        <button
          className={`salary-btn ${tab === 'history' ? '' : 'secondary'}`}
          style={{ borderRadius: '4px 4px 0 0' }}
          onClick={() => setTab('history')}
        >History {canViewAll ? '(all)' : '(mine)'}</button>
      </div>

      {tab === 'fill' && (
        <div>
          <div className="cs-sticky">
            Progress: <strong>{completedCount}/{totalCount}</strong> <label><input type="checkbox" checked={remaining} onChange={e => setRemaining(e.target.checked)} /> Remaining only</label>
          </div>

          {Object.entries(grouped).map(([groupName, sections]) => (
            <div key={groupName} style={{ marginBottom: '1.5rem' }}>
              {groupName && <h3 style={{ borderBottom: '2px solid #5b2828', paddingBottom: 4 }}>{groupName}</h3>}
              {sections.map((sec) => (
                <div key={sec.id} className="salary-card" style={{ marginBottom: '0.75rem' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>{sec.name}</h4>
                  {sec.items.map((it) => {
                    const r = responses[it.id] || { is_completed: false, notes: '' };
                    if (remaining && r.is_completed) return null;
                    return (
                      <div key={it.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.4rem' }}>
                        <input
                          id={`checklist-item-${it.id}`} type="checkbox"
                          checked={r.is_completed}
                          onChange={(e) => setItem(it.id, { is_completed: e.target.checked })}
                          style={{ marginTop: 4 }}
                        />
                        <div style={{ flex: 1 }}>
                          <label htmlFor={`checklist-item-${it.id}`} style={{ display: 'block', minHeight: 44, textDecoration: r.is_completed ? 'line-through' : 'none', color: r.is_completed ? '#888' : 'inherit' }}>
                            {it.label}
                          </label>
                          {it.hint && <small style={{ color: '#888' }}>{it.hint}</small>}
                          <button type="button" className="salary-btn secondary" aria-expanded={noteItems.has(it.id) || !!r.notes} onClick={() => setNoteItems(old => { const next = new Set(old); next.has(it.id) ? next.delete(it.id) : next.add(it.id); return next; })}>Notes / issue</button>
                          {(noteItems.has(it.id) || !!r.notes) && <input
                            aria-label={`Notes for ${it.label}`} type="text"
                            placeholder="Notes (optional)"
                            value={r.notes}
                            onChange={(e) => setItem(it.id, { notes: e.target.value })}
                            style={{ width: '100%', marginTop: 4 }}
                          />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}

          <div className="salary-card">
            <label style={{ display: 'block', marginBottom: 4 }}>Overall notes (optional)</label>
            <textarea
              rows={3}
              value={overallNotes}
              onChange={(e) => setOverallNotes(e.target.value)}
              style={{ width: '100%' }}
              placeholder="Anything broken? Need to escalate? Mention here."
            />
          </div>

          <button className="salary-btn" onClick={submit} disabled={saving} style={{ marginTop: '1rem' }}>
            {saving ? 'Submitting…' : `Submit (${completedCount}/${totalCount} done)`}
          </button>
        </div>
      )}

      {tab === 'history' && (
        <div>
          {history.length === 0 ? (
            <p style={{ color: '#666' }}>No submissions yet.</p>
          ) : (
            <DataTable
              rows={history}
              rowKey={(s) => s.id}
              caption="Submission history"
              empty="No submissions yet."
              columns={[
                { key: 'when', header: 'When', priority: 'primary', render: (s) => new Date(s.submitted_at).toLocaleString() },
                { key: 'by', header: 'By', priority: 'summary', render: (s) => s.submitted_by_name },
                { key: 'progress', header: 'Progress', priority: 'summary', numeric: true, render: (s) => `${s.completed_count}/${s.total_count}` },
              ]}
              actions={(s) => <button className="salary-btn secondary" onClick={() => openSubmission(s.id)}>View</button>}
            />
          )}
        </div>
      )}

      {view && (
        <div className="modal-backdrop" onClick={() => setView(null)}>
          <div className="modal-card" style={{ maxWidth: 720, maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3>Submission #{view.submission.id}</h3>
            <p style={{ color: '#666', marginTop: 0 }}>
              {new Date(view.submission.submitted_at).toLocaleString()} by <strong>{view.submission.submitted_by_name}</strong>
            </p>
            {view.submission.notes && (
              <div style={{ background: '#fff7e6', padding: '0.5rem', marginBottom: '0.5rem' }}>
                <strong>Notes:</strong> {view.submission.notes}
              </div>
            )}
            {view.responses.map((r) => (
              <div key={r.item_id} style={{ borderBottom: '1px solid #eee', padding: '0.4rem 0' }}>
                <div>{r.is_completed ? '✓' : '○'} {r.label}</div>
                <small style={{ color: '#888' }}>{r.group_name ? `${r.group_name} → ` : ''}{r.section_name}</small>
                {r.notes && <div style={{ color: '#555', fontStyle: 'italic', fontSize: '0.85em' }}>“{r.notes}”</div>}
              </div>
            ))}
            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button className="salary-btn secondary" onClick={() => setView(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistDetail;
